import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Save, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../lib/auth";
import { adminGetArticle, adminUpdateArticle, adminPublishArticle, adminUnpublishArticle, formatApiError } from "../lib/api";

export default function AdminArticleEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { logout } = useAuth();

    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState(null);

    const [form, setForm] = useState({ titolo: "", sommario: "", categoria: "", contenuto: "" });

    useEffect(() => {
        let mounted = true;
        adminGetArticle(id)
            .then((a) => {
                if (!mounted) return;
                setArticle(a);
                setForm({ titolo: a.titolo, sommario: a.sommario, categoria: a.categoria, contenuto: a.contenuto });
                setLoading(false);
            })
            .catch((err) => {
                if (mounted) { setError(formatApiError(err)); setLoading(false); }
            });
        return () => { mounted = false; };
    }, [id]);

    const handleSave = async () => {
        setSaving(true);
        setFeedback(null);
        try {
            const updated = await adminUpdateArticle(id, form);
            setArticle(updated);
            setFeedback({ type: "success", text: "Modifiche salvate" });
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        } finally {
            setSaving(false);
        }
    };

    const handlePublishToggle = async () => {
        try {
            if (article.status === "published") {
                const updated = await adminUnpublishArticle(id);
                setArticle(updated);
                setFeedback({ type: "success", text: "Articolo riportato in bozza" });
            } else {
                const updated = await adminPublishArticle(id);
                setArticle(updated);
                setFeedback({ type: "success", text: "Articolo pubblicato" });
            }
        } catch (err) {
            setFeedback({ type: "error", text: formatApiError(err) });
        }
    };

    const inputClass = "w-full bg-white border border-slate-300 px-4 py-3 text-navy focus:outline-none focus:border-gold transition-colors";
    const labelClass = "block text-[11px] uppercase tracking-[0.25em] font-bold text-navy mb-2";

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center text-slate-500 font-mono">Caricamento...</div>;
    }
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="text-red-600 mx-auto mb-3" size={40} />
                    <p className="text-red-600 mb-4">{error}</p>
                    <Link to="/admin" className="btn-navy">Torna alla dashboard</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-article-editor">
            <header className="bg-navy text-white">
                <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/admin" data-testid="editor-back" className="inline-flex items-center gap-2 text-white/80 hover:text-gold text-sm">
                        <ArrowLeft size={16} /> Dashboard
                    </Link>
                    <div className="flex items-center gap-3">
                        <span className={`inline-block px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold ${article.status === "published" ? "bg-green-500 text-white" : "bg-amber-500 text-navy"}`}>
                            {article.status === "published" ? "Pubblicato" : "Bozza"}
                        </span>
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
                <div className="flex items-center gap-4 mb-6">
                    <span className="gold-rule" />
                    <span className="label-eyebrow">Editor Articolo</span>
                </div>
                <h1 className="font-serif text-navy text-3xl md:text-4xl mb-10">
                    Modifica e revisione
                </h1>

                {feedback && (
                    <div className={`mb-6 p-4 border flex items-start gap-3 ${feedback.type === "success" ? "border-green-600 bg-green-50 text-green-800" : "border-red-500 bg-red-50 text-red-800"}`} data-testid="editor-feedback">
                        {feedback.type === "success" ? <CheckCircle2 size={18} className="mt-0.5" /> : <AlertCircle size={18} className="mt-0.5" />}
                        <span className="text-sm">{feedback.text}</span>
                    </div>
                )}

                <div className="bg-white border border-slate-200 p-8 space-y-6">
                    <div>
                        <label className={labelClass} htmlFor="titolo">Titolo</label>
                        <input id="titolo" data-testid="editor-titolo" className={inputClass} value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} />
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="categoria">Categoria</label>
                        <input id="categoria" data-testid="editor-categoria" className={inputClass} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} />
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="sommario">Sommario</label>
                        <textarea id="sommario" rows={2} data-testid="editor-sommario" className={inputClass} value={form.sommario} onChange={(e) => setForm({ ...form, sommario: e.target.value })} />
                    </div>
                    <div>
                        <label className={labelClass} htmlFor="contenuto">Contenuto (markdown)</label>
                        <textarea id="contenuto" rows={24} data-testid="editor-contenuto" className={`${inputClass} font-mono text-sm`} value={form.contenuto} onChange={(e) => setForm({ ...form, contenuto: e.target.value })} />
                        <p className="text-xs text-slate-500 mt-2">Markdown supportato: <code>## Titolo</code>, <code>**grassetto**</code>, <code>*corsivo*</code>, <code>- elenco</code></p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-200">
                        <button onClick={handleSave} disabled={saving} data-testid="editor-save" className="btn-navy">
                            {saving ? <><Loader2 size={16} className="animate-spin" /> Salvataggio...</> : <><Save size={16} /> Salva modifiche</>}
                        </button>
                        <button
                            onClick={handlePublishToggle}
                            data-testid="editor-publish-toggle"
                            className={article.status === "published" ? "btn-outline-navy" : "btn-gold"}
                        >
                            {article.status === "published" ? "Riporta in bozza" : "Pubblica articolo"}
                        </button>
                        {article.status === "published" && (
                            <Link to={`/articolo/${article.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-7 py-3 text-navy text-sm uppercase tracking-wide font-semibold hover:text-gold-deep">
                                Vedi sul sito →
                            </Link>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
